import { useEffect, useState } from 'react';
import { useAddItemsToCart, useCart } from 'hooks';
import classNames from 'classnames';
import { useSettings } from 'contexts/SettingContext';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useRouter } from 'next/router';
import { getCurrencyCode } from 'lib/shopify';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useGlobalModules } from 'contexts';
import Select from 'components/shared/select';
import BackInStockNotifier from '../../product/backInStockNotifier/backInStockNotifier';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import styles from './bundleCartSection.module.scss';

const BundleCartSection = ({ className, outOfStock, productBundleData, locale }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const router = useRouter();
  const addItems = useAddItemsToCart();
  const { addProduct } = useSettings();
  const { title, thumbnailImage, shopifyData, productSpecsCollection, slug } = productBundleData;
  const globalModules = useGlobalModules();
  const { addToCart, addingToCart } = globalModules.translations.button;
  const { content: oosContent, submit: oosSubmit, success: oosSuccess } =
    globalModules.translations.oos;
  const { linkToAnotherProductMessage, linkToAnotherProductButtonText } =
    globalModules.translations.product;
  const { linkToAnotherProductBackground, linkToAnotherProductText } = globalModules.colors;

  // This is to initialize the cart when the customer is navigating to a product page
  // eslint-disable-next-line
  const { cart } = useCart();

  const handleAddToCart = async () => {
    setIsLoading(true);

    const bundleVariantId = getShopifyGid('ProductVariant', shopifyData?.variants?.[0]?.id);

    const productsDataForCheckout = [
      {
        variantId: bundleVariantId,
        quantity,
      },
    ];

    try {
      await addItems(productsDataForCheckout);
    } catch (e) {
      // eslint-disable-next-line
      console.error(e); 
    }

    const productType = productSpecsCollection?.items?.find(item =>
      item.type.name.includes('Product Type')
    );

    const productCategory = productSpecsCollection?.items?.find(item =>
      item.type.name.includes('Product Category')
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useProductAddToCartTracking({
      product: {
        name: title,
        id: shopifyData?.variants?.[0]?.product_id,
        category: title,
        price: shopifyData.variants?.[0]?.price,
        itemListName: productType || productCategory,
        productSpecsCollection,
        quantity,
        currencyCode: getCurrencyCode(locale),
        image_url: thumbnailImage?.baseUrl,
        compare_at_price: shopifyData.variants?.[0]?.compare_at_price || null,
      },
    });
    setIsLoading(false);

    addProduct({
      title,
      slug,
      variantId: shopifyData?.variants?.[0]?.id,
    });
  };

  useEffect(() => {
    setQuantity(1);
  }, [router.asPath]);

  if (
    productBundleData?.linkToAnotherProduct?.slug &&
    productBundleData?.useLinkToAnotherProduct !== false
  ) {
    return (
      <div className={classNames(className, styles.linkToAnotherProduct)}>
        <p>{linkToAnotherProductMessage}</p>
        <SiteLink
          locale={locale}
          className={styles.linkToAnotherButton}
          style={{
            backgroundColor: linkToAnotherProductBackground,
            color: linkToAnotherProductText,
          }}
          href={addLocaleToHref(productBundleData?.linkToAnotherProduct?.slug, locale, router)}
        >
          {linkToAnotherProductButtonText}
        </SiteLink>
      </div>
    );
  }

  const selectOptions = [];
  for (let i = 1; i <= 10; i += 1) {
    selectOptions.push({ text: `${i}`, value: i });
  }

  return (
    <div className={classNames(className, styles.cartSection)}>
      {outOfStock ? (
        <BackInStockNotifier
          locale={locale}
          text={oosContent.json}
          successText={oosSuccess}
          submitText={oosSubmit}
          productIds={[shopifyData?.variants?.[0]?.id].filter(Boolean)}
        />
      ) : (
        <div className={styles.controls}>
          <Select
            className={styles.select}
            options={selectOptions}
            selected={quantity || selectOptions[0].value}
            onChange={value => setQuantity(parseInt(value, 10))}
          />
          <button
            type="button"
            className={styles.addToCartButton}
            onClick={() => {
              handleAddToCart();
            }}
          >
            {isLoading ? addingToCart : addToCart}
          </button>
        </div>
      )}
    </div>
  );
};

export default BundleCartSection;
